// بِسْمِ ٱللَّهِ ٱلرَّحْمَٰنِ ٱلرَّحِيمِ
//
// Templates wiring for the Бриф promotion console. SPEC §4.
//
// Server-backed CRUD + instantiate:
//   GET/POST   /api/publisher/templates
//   GET/PUT/DELETE /api/publisher/templates/:id
//   POST       /api/publisher/templates/:id/instantiate
//
// Plus the 5 client-side `starter-` templates (ported verbatim from the old
// PromotionPublisherPage) and a client-side {{var}} substitution helper so the
// starters can be turned into a SourceContent without a round-trip.
//
// Reads use softCall (mock fallback, never logs out); writes/instantiate use
// publisherCall (strict — real errors surface, 401 → logout).

import { publisherCall, softCall } from './client';
import type { Platform, PromotionTemplate, SourceContent } from './types';

// ── Starter (client-side) templates ──────────────────────────────
// Ported verbatim from STARTER_PROMOTION_TEMPLATES in PromotionPublisherPage.
// These never touch the worker — they live entirely client-side and are merged
// into the templates list with the `starter-` id prefix as the discriminator.

export const STARTER_TEMPLATES: PromotionTemplate[] = [
  {
    id: 'starter-weekly-summary-selection',
    name: 'Еженедельная подборка',
    description: 'Пост с несколькими свежими саммари по одной теме.',
    title_template: '{{тема}}: {{количество}} саммари на неделю',
    body_template:
      'Собрали {{количество}} саммари по теме «{{тема}}», чтобы быстро войти в контекст и выбрать, что читать дальше.\n\nПодборка подойдёт для: {{аудитория}}.\n\nОткрыть в Summio: {{ссылка}}',
    platforms: ['telegram', 'twitter'],
    variables: {
      тема: 'продуктивность и мышление',
      количество: '5',
      аудитория: 'тех, кто хочет учиться без лишнего шума',
      ссылка: 'https://summio.org',
      теги: 'саммари, обучение, продуктивность',
    },
  },
  {
    id: 'starter-trend-pulse',
    name: 'Трендовая тема',
    description: 'Быстрый пост вокруг популярной темы или повестки.',
    title_template: 'Что почитать про {{тема}}',
    body_template:
      'Тема «{{тема}}» снова в фокусе, поэтому подготовили короткую подборку саммари без воды.\n\nЧто внутри:\n• главные идеи\n• практические выводы\n• несколько углов зрения\n\n{{ссылка}}',
    platforms: ['telegram', 'twitter'],
    variables: {
      тема: 'AI-инструменты и личную эффективность',
      ссылка: 'https://summio.org',
      теги: 'саммари, тренды, ai',
    },
  },
  {
    id: 'starter-new-summary-launch',
    name: 'Новый саммари',
    description: 'Анонс одного свежего саммари с понятной пользой.',
    title_template: 'Новое саммари: {{название}}',
    body_template:
      'В Summio появилось новое саммари «{{название}}».\n\nПочему стоит открыть:\n• {{польза_1}}\n• {{польза_2}}\n• {{польза_3}}\n\nЧитать: {{ссылка}}',
    platforms: ['telegram', 'twitter'],
    variables: {
      название: 'Название саммари',
      польза_1: 'быстро понять главную идею',
      польза_2: 'забрать практичные выводы',
      польза_3: 'решить, читать ли полный источник',
      ссылка: 'https://summio.org',
      теги: 'саммари, чтение, развитие',
    },
  },
  {
    id: 'starter-app-return',
    name: 'Возврат в приложение',
    description: 'Мягкий in-app/push стиль для возвращения пользователя.',
    title_template: '{{количество}} саммари ждут тебя',
    body_template:
      'Мы подобрали {{количество}} саммари под твой интерес: {{тема}}.\n\nОткрой Summio, выбери одно и забери главные идеи за несколько минут.\n\n{{ссылка}}',
    platforms: ['telegram', 'twitter'],
    variables: {
      количество: '3',
      тема: 'карьера, мышление и полезные привычки',
      ссылка: 'https://summio.org',
      теги: 'Summio, привычки, обучение',
    },
  },
  {
    id: 'starter-short-hook',
    name: 'Короткий хук',
    description: 'Компактный формат для X.com и быстрых Telegram-постов.',
    title_template: '{{тема}} за 10 минут',
    body_template:
      'Если тема «{{тема}}» давно лежит в списке “потом”, начни с саммари.\n\n10 минут, главные идеи, понятный следующий шаг.\n\n{{ссылка}}',
    platforms: ['twitter', 'telegram'],
    variables: {
      тема: 'финансовое мышление',
      ссылка: 'https://summio.org',
      теги: 'саммари, чтение, фокус',
    },
  },
];

// ── Helpers ──────────────────────────────────────────────────────

/** A `starter-` id signals a client-side template (no worker round-trip). */
export function isStarterTemplate(template: Pick<PromotionTemplate, 'id'>): boolean {
  return template.id.startsWith('starter-');
}

/** Replace `{{ key }}` tokens with the matching value; unknown keys → ''. */
function fillTemplateText(template: string | undefined, values: Record<string, string>): string {
  if (!template) return '';
  return template.replace(/\{\{\s*([^}]+?)\s*\}\}/g, (_match, rawKey: string) => {
    const key = String(rawKey).trim();
    return values[key] ?? '';
  });
}

/**
 * Client-side instantiation of a starter template — mirrors the old
 * `instantiateStarterTemplate`. The `теги` variable is split on commas into the
 * tag list; `ссылка` (when non-empty) becomes the link.
 */
export function instantiateStarter(
  template: PromotionTemplate,
  variables: Record<string, string>,
): SourceContent {
  const tags = String(variables.теги ?? '')
    .split(',')
    .map((tag) => tag.trim())
    .filter(Boolean);
  const link = variables.ссылка?.trim();
  return {
    title: fillTemplateText(template.title_template, variables),
    body: fillTemplateText(template.body_template, variables),
    link: link || undefined,
    tags,
    language: 'ru',
  };
}

// ── Server-backed API ────────────────────────────────────────────

const TEMPLATES_PATH = '/api/publisher/templates';

/** Form payload for create/update — matches the worker contract (§4). */
export interface TemplateForm {
  id?: string;
  name: string;
  description?: string;
  title_template?: string;
  body_template: string;
  platforms: Platform[];
  variables: Record<string, string>;
}

/** GET /api/publisher/templates → `{ templates }`. SOFT read, fallback `[]`. */
export async function listTemplates(): Promise<PromotionTemplate[]> {
  const { data } = await softCall<PromotionTemplate[]>(
    TEMPLATES_PATH,
    [],
    (json) => {
      const list = (json as { templates?: unknown })?.templates;
      return Array.isArray(list) ? (list as PromotionTemplate[]) : [];
    },
  );
  return data;
}

/**
 * Create (POST) when `form.id` is absent, otherwise update (PUT
 * /api/publisher/templates/:id). STRICT — surfaces `name and body_template
 * required` (400) and other worker errors. Returns the saved template.
 */
export async function saveTemplate(form: TemplateForm): Promise<PromotionTemplate> {
  const body: Record<string, unknown> = {
    name: form.name,
    description: form.description,
    title_template: form.title_template,
    body_template: form.body_template,
    platforms: form.platforms,
    variables: form.variables,
  };

  const isUpdate = typeof form.id === 'string' && form.id.length > 0;
  const path = isUpdate ? `${TEMPLATES_PATH}/${encodeURIComponent(form.id as string)}` : TEMPLATES_PATH;

  const res = await publisherCall<{ template?: PromotionTemplate; error?: string }>(path, {
    method: isUpdate ? 'PUT' : 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

  if (!res.template) {
    throw new Error(res.error ?? 'template save failed');
  }
  return res.template;
}

/** DELETE /api/publisher/templates/:id → `{ ok:true }`. STRICT. */
export async function removeTemplate(id: string): Promise<void> {
  await publisherCall<{ ok?: boolean; error?: string }>(
    `${TEMPLATES_PATH}/${encodeURIComponent(id)}`,
    { method: 'DELETE' },
  );
}

/**
 * POST /api/publisher/templates/:id/instantiate `{ variables }` → `{ source }`.
 * STRICT — 404 when the template is unknown. For `starter-` ids this never hits
 * the worker; resolve them client-side with `instantiateStarter` instead.
 */
export async function instantiateTemplate(
  id: string,
  variables: Record<string, string>,
): Promise<SourceContent> {
  const res = await publisherCall<{ source?: SourceContent; error?: string }>(
    `${TEMPLATES_PATH}/${encodeURIComponent(id)}/instantiate`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ variables }),
    },
  );

  if (!res.source) {
    throw new Error(res.error ?? 'instantiate failed');
  }
  return res.source;
}
